"use client";

import { useState } from "react";
import { MapPin, Phone, Mail, Clock, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConsultFormContent() {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  const steps = [{
    title: "Deployment Scope",
    fields: ["projectType", "location"]
  }, {
    title: "Agent Configuration",
    fields: ["flowRate", "pressure", "systemType"]
  }, {
    title: "Technical Context",
    fields: ["blueprintFile"]
  }];
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/10 border border-primary/30 rounded-lg text-center backdrop-blur-md">
        <p className="text-primary font-default font-semibold mb-2">{successMessage}</p>
        <p className="text-muted-foreground font-mono text-sm mb-4">Our neural network has received your request. An architect will respond shortly.</p>
        <Button onClick={resetForm} variant="outline" className="w-full border-primary/50 text-primary hover:bg-primary/10 rounded-lg">
          Initialize New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {}
      <div className="flex gap-2">
        {steps.map((step, index) => <div key={index} data-index={index} className={`flex-1 h-2 rounded-lg transition-all duration-500 ${index <= currentStep ? "bg-primary shadow-[0_0_10px_rgba(0,255,244,0.5)]" : "bg-secondary"}`} />)}
      </div>

      {}
      <div>
        <h3 className="text-lg font-default font-bold text-foreground tracking-tight">
          {steps[currentStep].title}
        </h3>
        <p className="text-xs font-mono text-muted-foreground mt-1 uppercase tracking-widest">
          Phase {currentStep + 1} 
        </p>
      </div>

      {}
      {currentStep === 0 && <div className="space-y-4">
          <FormField control={control} name="projectType" rules={{
        required: "Industry type is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Industry Sector</FormLabel>
                <FormControl>
                  <select {...field} className="w-full h-10 px-3 rounded-md border border-border bg-secondary/50 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-all">
                    <option value="">Select sector</option>
                    <option value="enterprise">Enterprise IT</option>
                    <option value="saas">SaaS Support</option>
                    <option value="fintech">FinTech Infrastructure</option>
                  </select>
                </FormControl>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />

          <FormField control={control} name="location" rules={{
        required: "Region is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Operational Region</FormLabel>
                <FormControl>
                  <Input placeholder="Global or Specific City" className="bg-secondary/50 border-border focus-visible:ring-primary font-mono" {...field} />
                </FormControl>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />
        </div>}

      {}
      {currentStep === 1 && <div className="space-y-4">
          <FormField control={control} name="flowRate" rules={{
        required: "Ticket volume is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Monthly Ticket Volume</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="e.g., 5000" className="bg-secondary/50 border-border focus-visible:ring-primary font-mono" {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[10px]">Estimated concurrent support cases</FormDescription>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />

          <FormField control={control} name="pressure" rules={{
        required: "SLA requirement is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Target Response Time (Sec)</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="e.g., 30" className="bg-secondary/50 border-border focus-visible:ring-primary font-mono" {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[10px]">Maximum latency for AI initial response</FormDescription>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />

          <FormField control={control} name="systemType" rules={{
        required: "Agent type is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Agent Architecture</FormLabel>
                <FormControl>
                  <select {...field} className="w-full h-10 px-3 rounded-md border border-border bg-secondary/50 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary transition-all">
                    <option value="">Select architecture</option>
                    <option value="autonomous">Fully Autonomous</option>
                    <option value="hybrid">Human-in-the-loop</option>
                    <option value="triage">Triage & Routing Only</option>
                  </select>
                </FormControl>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />
        </div>}

      {}
      {currentStep === 2 && <div className="space-y-4">
          <FormField control={control} name="blueprintFile" rules={{
        required: "Documentation is required"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Knowledge Base / API Docs</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={e => {
              field.onChange(e.target.files?.[0]);
            }} className="bg-secondary/50 border-border file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:font-mono file:text-xs hover:file:bg-primary/80" />
                  </div>
                </FormControl>
                <FormDescription className="font-mono text-[10px]">
                  Upload system documentation for AI training (Max 10MB)
                </FormDescription>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />

          <FormField control={control} name="additionalNotes" render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-xs uppercase text-muted-foreground">Integration Requirements</FormLabel>
                <FormControl>
                  <textarea placeholder="Specify CRM, Slack, or custom API endpoints..." className="w-full min-h-24 px-3 py-2 rounded-md border border-border bg-secondary/50 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary font-mono text-sm" {...field} />
                </FormControl>
                <FormMessage className="text-destructive font-mono text-[10px]" />
              </FormItem>} />
        </div>}

      {}
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
          <p className="text-destructive font-mono text-xs">{submitError}</p>
        </div>}

      {}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 0} className="gap-2 border-border text-muted-foreground hover:text-foreground rounded-lg font-mono text-xs">
          <ChevronLeft className="size-4" />
          Back
        </Button>

        {currentStep < steps.length - 1 ? <Button type="button" onClick={handleNext} className="flex-1 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-default font-bold">
            Continue
            <ChevronRight className="size-4" />
          </Button> : <Button type="submit" disabled={isSubmitting} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-default font-bold">
            {isSubmitting ? "Processing..." : "Deploy Inquiry"}
          </Button>}
      </div>
    </Form>;
}
export default function ConnectWithAiTech() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  return <section data-section-id="265211" id="connect-with-ai-tech" className="bg-background text-foreground py-12 md:py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none select-none">
        <svg width="100%" height="100%" className="w-full h-full">
          <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M0 10 H40 V50 H60 V90 H100" fill="none" stroke="currentColor" strokeWidth="0.5" />
            <circle cx="40" cy="10" r="1.5" fill="currentColor" />
            <circle cx="60" cy="50" r="1.5" fill="currentColor" />
            <circle cx="100" cy="90" r="1.5" fill="currentColor" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#circuit-pattern)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
        {}
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tighter">
            Connect with AI Tech
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-mono text-sm">
            Transition from legacy human support to high-efficiency autonomous agents. Our architects are ready to scale your operations.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div ref={leftRef} className={`transition-all duration-700 cubic-bezier(0.19, 1, 0.22, 1) ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <Card className="h-full border border-border/50 bg-secondary/30 backdrop-blur-md shadow-xl rounded-lg">
              <CardHeader>
                <CardTitle className="text-3xl font-default font-bold tracking-tight">Global HQ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <MapPin className="size-5 text-primary mt-1 drop-shadow-[0_0_5px_rgba(0,255,244,0.5)]" />
                  </div>
                  <div>
                    <h3 className="font-mono text-xs uppercase text-muted-foreground mb-1 tracking-widest">
                      Physical Node
                    </h3>
                    <p className="text-foreground font-default text-lg leading-tight">
                      155 Princess St. Kangaroo Point,<br />QLD, 4169 Australia
                    </p>
                  </div>
                </div>

                {}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Phone className="size-5 text-primary mt-1 drop-shadow-[0_0_5px_rgba(0,255,244,0.5)]" />
                  </div>
                  <div>
                    <h3 className="font-mono text-xs uppercase text-muted-foreground mb-1 tracking-widest">
                      Voice Uplink
                    </h3>
                    <Link to="tel:0468075702" className="text-primary hover:text-primary/80 transition-colors font-mono text-lg">
                      0468 075 702
                    </Link>
                  </div>
                </div>

                {}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Mail className="size-5 text-primary mt-1 drop-shadow-[0_0_5px_rgba(0,255,244,0.5)]" />
                  </div>
                  <div>
                    <h3 className="font-mono text-xs uppercase text-muted-foreground mb-1 tracking-widest">
                      Data Stream
                    </h3>
                    <Link to="mailto:info@aitechsolution.io" className="text-primary hover:text-primary/80 transition-colors font-mono text-lg">
                      info@aitechsolution.io
                    </Link>
                  </div>
                </div>

                {}
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Clock className="size-5 text-primary mt-1 drop-shadow-[0_0_5px_rgba(0,255,244,0.5)]" />
                  </div>
                  <div>
                    <h3 className="font-mono text-xs uppercase text-muted-foreground mb-1 tracking-widest">
                      Uptime
                    </h3>
                    <p className="text-foreground font-mono text-sm">
                      24/7 Autonomous Monitoring<br />
                      Human Support: Mon-Fri 8am-6pm
                    </p>
                  </div>
                </div>

                {}
                <div className="mt-8 p-4 bg-primary/5 border border-primary/20 rounded-lg backdrop-blur-sm">
                  <p className="text-sm text-foreground font-mono leading-relaxed">
                    Our AI agents maintain a <strong className="text-primary">99.9% resolution rate</strong> on complex technical cases. Reduce overhead by up to 85% while increasing customer satisfaction.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-700 cubic-bezier(0.19, 1, 0.22, 1) ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <Card className="border border-border/50 bg-secondary/30 backdrop-blur-md shadow-xl rounded-lg">
              <CardHeader>
                <CardTitle className="text-3xl font-default font-bold tracking-tight">System Inquiry</CardTitle>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="connect_form_support_hub" formId="connect_form_support_hub" sectionName="consult-expert" defaultValues={{
                projectType: "",
                location: "",
                flowRate: "",
                pressure: "",
                systemType: "",
                blueprintFile: null,
                additionalNotes: ""
              }} validationRules={{
                projectType: {
                  required: true
                },
                location: {
                  required: true,
                  minLength: 2
                },
                flowRate: {
                  required: true
                },
                pressure: {
                  required: true
                },
                systemType: {
                  required: true
                },
                blueprintFile: {
                  required: true
                }
              }} submitText="Deploy Inquiry" successMessage="Transmission successful. Our AI architects are analyzing your requirements.">
                  <ConsultFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}